// =============================================================================
// Public Booking Layout — header con branding + container responsive
// =============================================================================
// Due stili a discrezione del brand (Tenant.settings.publicBooking.branding.theme):
//   - "minimal": header compatto pulito, sfondo con leggera velatura brand
//   - "hero":    header d'impatto con copertina/gradiente brand + titolo di
//                benvenuto; la card del wizard si sovrappone leggermente all'hero
// Il colore brand arriva da --skedulab-public-primary (impostata dalla page).
// =============================================================================

import { ReactNode, RefObject } from 'react';
import type { PublicTenantInfo } from '../../api/public-booking-hooks';
import { resolveOrgIcon } from '../common/OrgAvatar';
import { resolveAssetUrl } from '../../api/client';

interface Props {
  tenant: PublicTenantInfo | null;
  children: ReactNode;
  /// True quando la pagina è renderizzata dentro l'iframe del widget embed:
  /// rimuove header + branding per dare all'host page il controllo visivo.
  embed?: boolean;
  containerRef?: RefObject<HTMLDivElement>;
}

const PRIMARY = 'var(--skedulab-public-primary, #10B981)';

export function PublicBookingLayout({ tenant, children, embed, containerRef }: Props) {
  if (embed) {
    return (
      <div ref={containerRef} className="w-full bg-white">
        <main className="px-4 py-4">
          <div className="relative">{children}</div>
        </main>
      </div>
    );
  }

  const branding = tenant?.branding;
  const theme = branding?.theme ?? 'minimal';
  const displayName = tenant?.displayName ?? 'Skedulab';
  // Il logo dell'organizzazione arriva come path sull'API: va risolto sull'host
  // dell'API perché la pagina pubblica (e il widget) girano su un altro origin.
  const logoUrl = resolveAssetUrl(branding?.logoUrl);
  // Senza logo l'organizzazione si presenta con la sua icona standard
  const BrandIcon = resolveOrgIcon(branding?.icon);

  const logo = logoUrl ? (
    <img
      src={logoUrl}
      alt={displayName}
      className="w-11 h-11 rounded-xl object-cover shadow-sm ring-1 ring-black/5"
    />
  ) : null;

  // ---------------------------------------------------------------------------
  // HERO
  // ---------------------------------------------------------------------------
  if (theme === 'hero') {
    const coverStyle = branding?.coverImageUrl
      ? {
          backgroundImage: `linear-gradient(135deg, color-mix(in srgb, ${PRIMARY} 78%, transparent), color-mix(in srgb, ${PRIMARY} 30%, transparent)), url(${branding.coverImageUrl})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }
      : {
          backgroundImage: `linear-gradient(135deg, ${PRIMARY}, color-mix(in srgb, ${PRIMARY} 55%, #000))`,
        };

    return (
      <div className="min-h-screen h-full w-full bg-gray-50 overflow-y-auto">
        <header className="relative text-white" style={coverStyle}>
          <div className="absolute inset-0 bg-black/10" />
          <div className="relative max-w-3xl mx-auto px-5 pt-10 pb-20">
            <div className="flex items-center gap-3">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={displayName}
                  className="w-11 h-11 rounded-xl object-cover shadow-md ring-1 ring-white/30"
                />
              ) : (
                <div className="w-11 h-11 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-white ring-1 ring-white/30">
                  <BrandIcon className="w-6 h-6" />
                </div>
              )}
              <span className="text-sm font-semibold tracking-wide text-white/95 drop-shadow-sm">
                {displayName}
              </span>
            </div>
            <h1 className="mt-6 text-2xl sm:text-3xl font-bold leading-tight drop-shadow-sm">
              {branding?.headline || displayName}
            </h1>
            {branding?.subheadline && (
              <p className="mt-2 text-sm sm:text-base text-white/90 max-w-xl drop-shadow-sm">
                {branding.subheadline}
              </p>
            )}
          </div>
        </header>

        <main className="max-w-3xl mx-auto px-4 -mt-12 pb-12">
          <div className="bg-white rounded-2xl shadow-lg ring-1 ring-black/5 p-6 sm:p-7 relative">
            {children}
          </div>
          <PoweredBy />
        </main>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // MINIMAL (default) — con leggera velatura brand in cima
  // ---------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen h-full w-full overflow-y-auto"
      style={{
        background: `linear-gradient(180deg, color-mix(in srgb, ${PRIMARY} 7%, #f9fafb) 0%, #f9fafb 260px)`,
      }}
    >
      <header className="bg-white/80 backdrop-blur border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center gap-3">
          {logo ?? (
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-sm"
              style={{ background: PRIMARY }}
            >
              <BrandIcon className="w-6 h-6" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-base font-semibold text-gray-900 truncate">{displayName}</h1>
            {branding?.subheadline && (
              <p className="text-xs text-gray-500 truncate">{branding.subheadline}</p>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {branding?.headline && (
          <h2 className="mb-4 text-xl font-bold text-gray-900">{branding.headline}</h2>
        )}
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 p-6 sm:p-7 relative">
          {children}
        </div>
        <PoweredBy />
      </main>
    </div>
  );
}

function PoweredBy() {
  return (
    <p className="mt-4 text-center text-xs text-gray-400">
      Powered by <span className="font-medium text-gray-500">Skedulab</span>
    </p>
  );
}
